
import React, { useState, useEffect, useRef } from 'react';

import DashboardPage from './components/DashboardPage';
import PublicTranscriptPage from './components/PublicTranscriptPage';
import SettingsPage from './components/SettingsPage';
import { KeyRound, Cloud, Check } from 'lucide-react';
import { ThemeProvider } from './contexts/ThemeContext';
import { TranscriptionModeProvider } from './contexts/TranscriptionModeContext';

const ApiKeyErrorScreen = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-base-100 dark:bg-dark-100 p-4 text-center">
    <div className="bg-base-200 dark:bg-dark-200 p-8 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 max-w-md w-full">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20 text-red-500 mb-6">
        <KeyRound size={40} />
      </div>
      <h1 className="text-2xl font-bold text-base-content dark:text-dark-content mb-2">API Key Not Found</h1>
      <p className="text-base-content/80 dark:text-base-300">
        This application requires a valid API key to function. Please ensure the <code>API_KEY</code> environment variable is set before running the application.
      </p>
    </div>
  </div>
);

const DevUploadToast = () => {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    const onUploadSuccess = () => {
      setShow(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setShow(false), 3000);
    };
    
    window.addEventListener('cloud-upload-success', onUploadSuccess);
    return () => {
      window.removeEventListener('cloud-upload-success', onUploadSuccess);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] animate-fade-in-up">
      <div className="bg-green-600 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-green-500/50 backdrop-blur-md">
        <div className="p-1.5 bg-white/20 rounded-full flex items-center justify-center">
            <Cloud size={16} className="text-white" />
        </div>
        <div>
            <p className="text-sm font-bold flex items-center gap-2">
                Upload Complete <Check size={14} />
            </p>
            <p className="text-[10px] opacity-90 uppercase tracking-wide font-medium">File synced to Cloud Storage</p>
        </div>
      </div>
    </div>
  );
};

// Detect public share route: /s/[shortId]
const getPublicShortId = (): string | null => {
  const match = window.location.pathname.match(/^\/s\/([A-Za-z0-9]{1,64})$/);
  return match ? match[1] : null;
};

// Detect /settings route
const isSettingsRoute = (): boolean => window.location.pathname === '/settings';

const App: React.FC = () => {
  const shortId = getPublicShortId();

  if (shortId) {
    return (
      <ThemeProvider>
        <PublicTranscriptPage shortId={shortId} />
      </ThemeProvider>
    );
  }

  // /settings route — standalone settings page with back navigation to dashboard
  if (isSettingsRoute()) {
    return (
      <ThemeProvider>
        <div className="flex flex-col items-center justify-start w-full min-h-screen bg-base-100 dark:bg-dark-100 transition-colors duration-300">
          <main className="w-full pt-8 flex justify-center min-h-screen">
            <SettingsPage onBack={() => { window.location.href = '/'; }} />
          </main>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <TranscriptionModeProvider>
        <DashboardPage />
        <DevUploadToast />
      </TranscriptionModeProvider>
    </ThemeProvider>
  );
};

export default App;
